import { MapPin, Lock, Truck, Battery, Grid3X3, Building2, ArrowUpRight, Zap, Globe, Shield, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/animations/ScrollAnimations";

const solutions = [
  {
    id: "public",
    title: "Public Charging",
    subtitle: "Infrastructure",
    description: "Deploy high-visibility stations at retail, highways, and urban hubs. Multiple payment options, 1 Megawatt fast charging.",
    icon: MapPin,
    stats: { value: "Megawatt", label: "Max Power" },
    color: "primary",
    size: "large",
  },
  {
    id: "private",
    title: "Private Charging",
    subtitle: "Access Control",
    description: "Workplace and residential solutions with restricted access and flexible billing.",
    icon: Lock,
    stats: { value: "100%", label: "Controlled" },
    color: "accent",
    size: "medium",
  },
  {
    id: "fleet",
    title: "Fleet Management",
    subtitle: "Depot Optimization",
    description: "Smart depot charging, route optimization, TCO analytics for commercial fleets.",
    icon: Truck,
    stats: { value: "40%", label: "Cost Reduction" },
    color: "primary",
    size: "medium",
  },
  {
    id: "v2g",
    title: "V2G Technology",
    subtitle: "Bidirectional",
    description: "Turn EVs into grid assets. Generate revenue through energy arbitrage and demand response.",
    icon: Battery,
    stats: { value: "$5K+", label: "Annual Revenue/Vehicle" },
    color: "accent",
    size: "large",
  },
  {
    id: "grid",
    title: "Grid Integration",
    subtitle: "Smart Load",
    description: "OpenADR 2.0 certified. Demand response and load management for grid stability.",
    icon: Grid3X3,
    stats: { value: "OpenADR", label: "Certified" },
    color: "primary",
    size: "small",
  },
  {
    id: "cpo",
    title: "CPO Services",
    subtitle: "Full Turnkey",
    description: "Site selection to maintenance. Your brand, our expertise.",
    icon: Building2,
    stats: { value: "End-to-End", label: "Solution" },
    color: "accent",
    size: "small",
  },
];

export function SolutionsBentoGrid() {
  return (
    <section id="solutions-grid" className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-subtle" />
      <div className="absolute top-0 left-0 w-full section-divider" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mb-16 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <ScrollReveal>
            <motion.div 
              className="inline-flex items-center gap-2 badge-primary mb-6"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Solutions
            </motion.div>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Our <span className="text-gradient">Solutions</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-muted-foreground max-w-md text-lg font-light leading-relaxed">
              Comprehensive charging solutions designed for every use case, from single locations to nationwide networks.
            </p>
          </ScrollReveal>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 auto-rows-[minmax(220px,auto)]" staggerDelay={0.08}>
          {solutions.map((solution) => {
            const isLarge = solution.size === "large";
            const isMedium = solution.size === "medium";

            return (
              <StaggerItem key={solution.id}>
                <motion.a
                  href={`#${solution.id}-detail`}
                  whileHover={{ scale: 1.015, y: -6 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className={`
                    group relative overflow-hidden rounded-3xl border border-border/40 
                    bg-gradient-to-br from-card/80 via-card/60 to-card/40 backdrop-blur-sm
                    hover:border-${solution.color}/40 transition-all duration-500
                    ${isLarge ? "lg:col-span-2 lg:row-span-2" : ""}
                    ${isMedium ? "lg:row-span-2" : ""}
                    p-7 md:p-9 flex flex-col block h-full
                  `}
                >
                  <motion.div
                    className={`absolute -top-32 -right-32 w-64 h-64 bg-${solution.color}/15 rounded-full blur-3xl transition-opacity duration-500`}
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                  />
                  
                  <div className="absolute inset-0 opacity-[0.03] grid-pattern pointer-events-none" />

                  <div className="flex items-start justify-between mb-6 relative z-10">
                    <motion.div
                      className={`w-14 h-14 rounded-2xl bg-${solution.color}/10 border border-${solution.color}/20 flex items-center justify-center`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      <solution.icon className={`h-7 w-7 text-${solution.color}`} />
                    </motion.div>
                    <motion.div
                      className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <ArrowUpRight className="h-4 w-4 text-white/60 group-hover:text-white" />
                    </motion.div>
                  </div>

                  <div className="flex-1 flex flex-col relative z-10">
                    <span className={`text-xs uppercase tracking-wider text-${solution.color} mb-2 font-semibold`}>
                      {solution.subtitle}
                    </span>
                    <h3 className={`font-bold mb-3 tracking-tight text-white ${isLarge ? "text-2xl md:text-3xl" : "text-xl"}`}>
                      {solution.title}
                    </h3>
                    <p className={`text-white/60 flex-1 ${isLarge ? "text-base" : "text-sm"} leading-relaxed font-light`}>
                      {solution.description}
                    </p>
                  </div>

                  <div className="mt-6 pt-5 border-t border-white/10 relative z-10">
                    <div className={`text-2xl md:text-3xl font-bold text-${solution.color} tracking-tight`}>
                      {solution.stats.value}
                    </div>
                    <div className="text-xs text-white/50 uppercase tracking-wider font-medium">{solution.stats.label}</div>
                  </div>
                </motion.a>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>

      <ScrollReveal delay={0.4} className="container mx-auto px-4 sm:px-6 lg:px-8 mt-16 relative z-10">
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { icon: Zap, text: "OCPP 1.6 & 2.0.1" },
            { icon: Globe, text: "Global Roaming" },
            { icon: Shield, text: "FedRAMP Authorized" },
          ].map((feature, idx) => (
            <motion.div
              key={idx}
              className="flex items-center gap-2 glass-card px-6 py-3"
              whileHover={{ scale: 1.05 }}
            >
              <feature.icon className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-white">{feature.text}</span>
            </motion.div>
          ))}
        </div>
      </ScrollReveal>
      
      <div className="absolute bottom-0 left-0 w-full section-divider" />
    </section>
  );
}